import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Search, MapPin, AlertTriangle, ShoppingCart, Edit2, Loader2, Package, X } from "lucide-react";
import DualPrice, { convertToSEK } from "@/components/DualPrice";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Simplified, embedded "Lager" (inventory cards) and "Inköpspris" (price table) views
// used inside /purchasing/orders. The full-featured pages live at /inventory and /purchasing/prices.
export default function PurchasingLookup({ mode, suppliers = [], exchangeRate = 11.5, onOrder, search = "", onSearchChange }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setQuery((search || "").trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        if (mode === "lager") {
          const params = new URLSearchParams();
          if (query) params.append("search", query);
          params.append("include_zero_stock", "true");
          params.append("limit", "200");
          const res = await axios.get(`${API}/inventory?${params}`);
          if (!cancelled) { setItems(res.data.items || []); setTotal(res.data.total || 0); }
        } else {
          const params = { skip: 0, limit: 200 };
          if (query) params.search = query;
          const res = await axios.get(`${API}/purchasing/prices/all-products`, { params });
          if (!cancelled) { setItems(res.data.products || []); setTotal(res.data.total || 0); }
        }
      } catch (e) {
        if (!cancelled) { setItems([]); setTotal(0); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [mode, query]);

  const supplierName = (id) => suppliers.find(s => s.id === id)?.company_name || "Okänd";

  const pricesOf = (product) =>
    Object.entries(product.prices || {}).map(([sid, pi]) => ({ ...pi, supplier_id: sid }));

  const lowestOf = (product) => {
    const avail = pricesOf(product).filter(p => !p.unavailable && (p.purchase_price || 0) > 0);
    if (avail.length === 0) return null;
    return avail.reduce((best, p) =>
      convertToSEK(p.purchase_price, p.currency, exchangeRate) < convertToSEK(best.purchase_price, best.currency, exchangeRate) ? p : best
    );
  };

  const placeholder = mode === "lager"
    ? "Sök i lagret (artikelnummer, EAN, namn)..."
    : "Sök inköpspriser (artikelnummer, EAN, namn)...";

  return (
    <div className="space-y-4" data-testid={`lookup-${mode}`}>
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => onSearchChange && onSearchChange(e.target.value)}
              placeholder={placeholder}
              className="pl-10"
              data-testid="lookup-search"
              autoFocus
            />
          </div>
          <p className="text-xs text-muted-foreground mt-2" data-testid="lookup-count">
            {loading ? "Laddar..." : `Visar ${items.length}${total > items.length ? ` av ${total}` : ""} produkter${query ? " (filtrerat)" : ""}`}
          </p>
        </CardContent>
      </Card>

      {loading && items.length === 0 ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Package className="h-10 w-10 mx-auto mb-2 opacity-40" />
          Inga produkter
        </div>
      ) : mode === "lager" ? (
        <div className="space-y-2">
          {items.map((item) => {
            const totalQty = item.total_antal ?? item.antal ?? 0;
            const out = totalQty <= 0;
            const locs = (item.locations || []).filter(l => l.antal > 0);
            return (
              <Card
                key={item.id || item.ean}
                className={`cursor-pointer hover:border-primary/50 transition-colors border-l-4 ${out ? "border-l-red-500" : "border-l-green-500"}`}
                onClick={() => navigate(`/inventory/${item.ean}`)}
                data-testid={`lookup-lager-item-${item.ean}`}
              >
                <CardContent className="p-4 flex items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    {(item.tillverkare || item.kategori) && (
                      <div className="flex flex-wrap items-center gap-2 text-xs mb-1">
                        {item.tillverkare && <Badge variant="secondary" className="text-xs">{item.tillverkare}</Badge>}
                        {item.kategori && <Badge variant="secondary" className="text-xs">{item.kategori}</Badge>}
                      </div>
                    )}
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h3 className="font-heading font-bold truncate">{item.artikelnummer || item.ean}</h3>
                      <Badge variant="outline" className={`shrink-0 ${out ? "bg-red-500/10 border-red-500/30 text-red-500" : "bg-green-500/10 border-green-500/30 text-green-500"}`}>
                        {out && <AlertTriangle className="h-3 w-3 mr-1" />}{totalQty} st totalt
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground truncate">{item.produktnamn}</p>
                    <div className="flex flex-wrap items-center gap-2 text-xs mt-1">
                      {locs.length > 0 ? (
                        <span className="flex items-center gap-1 flex-wrap">
                          <MapPin className="h-3 w-3 text-muted-foreground" />
                          {locs.map((loc, idx) => (
                            <span key={loc.lagerplats} className="inline-flex items-center">
                              <span className="font-mono bg-muted px-1.5 py-0.5 rounded text-foreground">{loc.lagerplats}</span>
                              <span className="text-muted-foreground ml-0.5">({loc.antal})</span>
                              {idx < locs.length - 1 && <span className="text-muted-foreground mx-1">•</span>}
                            </span>
                          ))}
                        </span>
                      ) : (
                        <span className="italic text-muted-foreground">ingen lagerplats</span>
                      )}
                    </div>
                  </div>
                  <span className="text-xs text-muted-foreground font-mono shrink-0">{item.ean}</span>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="hidden md:grid grid-cols-[160px_130px_80px_70px_1fr_90px_90px_90px] gap-3 p-3 border-b bg-muted/50 text-sm font-medium">
              <div>Artikel</div><div>EAN</div><div>Kem. skatt</div><div>Frakt</div>
              <div>Inköpspris</div><div>Totalpris</div><div></div><div></div>
            </div>
            <div className="divide-y">
              {items.map((product) => {
                const lp = lowestOf(product);
                const count = pricesOf(product).filter(p => !p.unavailable && (p.purchase_price || 0) > 0).length;
                const unavail = pricesOf(product).filter(p => p.unavailable);
                const chemTax = Object.values(product.prices || {}).find(p => p?.chemical_tax)?.chemical_tax || 0;
                const freight = product.freight_cost || 0;
                const baseSEK = lp ? convertToSEK(lp.purchase_price, lp.currency, exchangeRate) : 0;
                const totalPrice = baseSEK + chemTax + freight;
                return (
                  <div key={product.ean} className="grid grid-cols-2 md:grid-cols-[160px_130px_80px_70px_1fr_90px_90px_90px] gap-3 p-3 items-center hover:bg-muted/30 text-sm" data-testid={`lookup-price-row-${product.ean}`}>
                    <div>
                      <Link to={`/inventory/${product.ean}`} className="font-mono font-medium text-primary hover:underline">
                        {product.artikelnummer || product.ean}
                      </Link>
                    </div>
                    <div className="text-xs text-muted-foreground font-mono">{product.ean}</div>
                    <div>{chemTax > 0 ? `${Math.round(chemTax)} kr` : <span className="text-muted-foreground">—</span>}</div>
                    <div>{freight > 0 ? <span className="text-blue-500 font-medium">{Math.round(freight)} kr</span> : <span className="text-muted-foreground">—</span>}</div>
                    <div>
                      {lp ? (
                        <div className="flex items-center gap-2 flex-wrap">
                          <DualPrice price={lp.purchase_price} currency={lp.currency} rate={exchangeRate} align="left" />
                          <span className="text-xs text-muted-foreground">({supplierName(lp.supplier_id)})</span>
                          <Badge variant="secondary" className="text-xs">{count} {count > 1 ? "priser" : "pris"}</Badge>
                        </div>
                      ) : unavail.length > 0 ? (
                        <div className="flex items-center gap-1 flex-wrap">
                          {unavail.map(u => (
                            <Badge key={u.supplier_id} variant="outline" className="text-red-600 border-red-500/50 bg-red-500/5 text-xs gap-1" title={u.unavailable_note}>
                              <X className="h-3 w-3" />{supplierName(u.supplier_id)}: Otillgänglig
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">Inget pris</span>
                      )}
                    </div>
                    <div className="font-bold text-emerald-500">{totalPrice > 0 ? `${Math.round(totalPrice).toLocaleString("sv-SE")} kr` : <span className="font-normal text-muted-foreground">—</span>}</div>
                    <div>
                      <Button variant="outline" size="sm" className="gap-1 text-xs" data-testid={`lookup-order-${product.ean}`}
                        onClick={() => onOrder && onOrder(product)}>
                        <ShoppingCart className="h-3 w-3" />Beställ
                      </Button>
                    </div>
                    <div>
                      <Button variant="ghost" size="sm" className="gap-1 text-xs" data-testid={`lookup-edit-${product.ean}`}
                        onClick={() => navigate(`/purchasing/prices?search=${encodeURIComponent(product.ean)}`)}>
                        <Edit2 className="h-3 w-3" />Redigera
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
